import type { SmartCondition, SmartField, SmartOp, SmartRules, SmartSort } from '@pujolisique/shared';

/* ------------------------------ Définition des critères ------------------------------ */

export type Input = 'text' | 'genre' | 'number' | 'year' | 'none' | 'quality' | 'library';
export interface FieldDef {
  label: string;
  ops: { op: SmartOp; label: string; input?: Input; unit?: string }[];
}

export const FIELDS: Record<SmartField, FieldDef> = {
  genre: { label: 'Genre', ops: [{ op: 'is', label: 'est', input: 'genre' }, { op: 'isNot', label: 'n’est pas', input: 'genre' }] },
  theme: { label: 'Thème', ops: [{ op: 'is', label: 'est', input: 'text' }, { op: 'isNot', label: 'n’est pas', input: 'text' }] },
  artist: { label: 'Artiste', ops: [{ op: 'is', label: 'contient', input: 'text' }, { op: 'isNot', label: 'ne contient pas', input: 'text' }] },
  composer: { label: 'Compositeur', ops: [{ op: 'is', label: 'contient', input: 'text' }, { op: 'isNot', label: 'ne contient pas', input: 'text' }] },
  album: { label: 'Album', ops: [{ op: 'contains', label: 'contient', input: 'text' }, { op: 'isNot', label: 'ne contient pas', input: 'text' }] },
  title: { label: 'Titre', ops: [{ op: 'contains', label: 'contient', input: 'text' }, { op: 'isNot', label: 'ne contient pas', input: 'text' }] },
  year: {
    label: 'Année',
    ops: [
      { op: 'between', label: 'entre', input: 'year' },
      { op: 'atLeast', label: 'à partir de', input: 'number' },
      { op: 'atMost', label: 'jusqu’à', input: 'number' },
    ],
  },
  added: { label: 'Ajouté', ops: [{ op: 'within', label: 'dans les derniers', input: 'number', unit: 'jours' }] },
  plays: {
    label: 'Mes écoutes',
    ops: [
      { op: 'never', label: 'jamais écouté', input: 'none' },
      { op: 'atLeast', label: 'au moins', input: 'number', unit: 'fois' },
      { op: 'atMost', label: 'au plus', input: 'number', unit: 'fois' },
    ],
  },
  familyPlays: {
    label: 'Écoutes de la famille',
    ops: [
      { op: 'never', label: 'jamais écouté', input: 'none' },
      { op: 'atLeast', label: 'au moins', input: 'number', unit: 'fois' },
      { op: 'atMost', label: 'au plus', input: 'number', unit: 'fois' },
    ],
  },
  notPlayedFor: { label: 'Pas écouté par moi depuis', ops: [{ op: 'within', label: '', input: 'number', unit: 'jours' }] },
  favorite: { label: 'Mes favoris', ops: [{ op: 'is', label: 'en font partie', input: 'none' }, { op: 'isNot', label: 'n’en font pas partie', input: 'none' }] },
  familyFavorite: { label: 'Favori de', ops: [{ op: 'atLeast', label: 'au moins', input: 'number', unit: 'personne(s)' }] },
  quality: { label: 'Qualité', ops: [{ op: 'is', label: 'est', input: 'quality' }] },
  duration: {
    label: 'Durée',
    ops: [
      { op: 'atLeast', label: 'au moins', input: 'number', unit: 'min' },
      { op: 'atMost', label: 'au plus', input: 'number', unit: 'min' },
    ],
  },
  library: { label: 'Bibliothèque', ops: [{ op: 'is', label: 'est', input: 'library' }] },
};

export const QUALITIES = [
  { v: 'lossless', label: 'sans perte (FLAC…)' },
  { v: 'hires', label: 'haute résolution' },
  { v: 'lossy', label: 'compressée (MP3…)' },
];

export const SORTS: { v: SmartSort; label: string }[] = [
  { v: 'random', label: 'Au hasard' },
  { v: 'added', label: 'Ajoutés récemment d’abord' },
  { v: 'title', label: 'Par titre' },
  { v: 'artist', label: 'Par artiste et album' },
  { v: 'year', label: 'Par année (récents d’abord)' },
  { v: 'mostPlayed', label: 'Mes plus écoutés d’abord' },
  { v: 'leastPlayed', label: 'Mes moins écoutés d’abord' },
  { v: 'lastPlayed', label: 'Écoutés récemment d’abord' },
];

export const DEFAULTS: Partial<Record<SmartField, Partial<SmartCondition>>> = {
  year: { value: 1990, value2: 1999 },
  added: { value: 30 },
  plays: {},
  familyPlays: { value: 3 },
  notPlayedFor: { value: 365 },
  familyFavorite: { value: 2 },
  quality: { value: 'lossless' },
  duration: { value: 5 },
};

export const PRESETS: { name: string; rules: SmartRules }[] = [
  { name: 'Jamais écoutés', rules: { match: 'all', conditions: [{ field: 'plays', op: 'never' }], sort: 'random', limit: 100 } },
  { name: 'Ajoutés ce mois-ci', rules: { match: 'all', conditions: [{ field: 'added', op: 'within', value: 30 }], sort: 'added', limit: 200 } },
  { name: 'Favoris de la famille', rules: { match: 'all', conditions: [{ field: 'familyFavorite', op: 'atLeast', value: 2 }], sort: 'random', limit: 200 } },
  { name: 'À redécouvrir', rules: { match: 'all', conditions: [{ field: 'plays', op: 'atLeast', value: 1 }, { field: 'notPlayedFor', op: 'within', value: 365 }], sort: 'random', limit: 100 } },
  { name: 'Mes plus écoutés', rules: { match: 'all', conditions: [{ field: 'plays', op: 'atLeast', value: 3 }], sort: 'mostPlayed', limit: 50 } },
  { name: 'Haute résolution', rules: { match: 'all', conditions: [{ field: 'quality', op: 'is', value: 'hires' }], sort: 'random', limit: 200 } },
  { name: 'Années 80', rules: { match: 'all', conditions: [{ field: 'year', op: 'between', value: 1980, value2: 1989 }], sort: 'random', limit: 200 } },
  {
    name: 'Noël',
    rules: {
      match: 'any',
      conditions: [
        { field: 'theme', op: 'is', value: 'Noël' },
        { field: 'title', op: 'contains', value: 'noël' },
        { field: 'genre', op: 'is', value: 'Christmas' },
      ],
      sort: 'random',
      limit: 200,
    },
  },
];

export const EMPTY: SmartRules = { match: 'all', conditions: [{ field: 'plays', op: 'never' }], sort: 'random', limit: 100 };

/** Condition complète (sans champ vide) */
export function complete(c: SmartCondition) {
  const input = FIELDS[c.field].ops.find((o) => o.op === c.op)?.input ?? 'none';
  if (input === 'none') return true;
  if (input === 'year') return Number.isFinite(Number(c.value)) && Number.isFinite(Number(c.value2));
  return c.value !== undefined && String(c.value).trim() !== '';
}

/** « Mes écoutes : jamais écouté ; Année entre 1980 et 1989 » */
export function describeRules(rules: SmartRules, libName: (id: string) => string = (x) => x): string {
  const parts = rules.conditions.map((c) => {
    const f = FIELDS[c.field];
    const o = f.ops.find((x) => x.op === c.op);
    let v = '';
    if (o?.input === 'year') v = `${c.value} et ${c.value2}`;
    else if (o?.input === 'quality') v = QUALITIES.find((q) => q.v === c.value)?.label ?? String(c.value);
    else if (o?.input === 'library') v = libName(String(c.value));
    else if (o?.input !== 'none') v = `${c.value}${o?.unit ? ` ${o.unit}` : ''}`;
    return `${f.label} ${o?.label ?? ''} ${v}`.replace(/\s+/g, ' ').trim();
  });
  const joined = parts.join(rules.match === 'any' ? ' ou ' : ' ; ');
  return `${joined || 'Tous les titres'} · ${SORTS.find((s) => s.v === rules.sort)?.label.toLowerCase()} · ${rules.limit} titres au plus`;
}
